import { useCity } from '../contexts/CityContext';
import { logApiRequest, logApiResponse, logApiError } from '../utils/logUtils';

// URL loglaması için bir kere gösterilecek flag
let apiUrlLogShown = false;
let serviceUrlCounter = 0;
const MAX_URL_LOGS = 2;

// İstek önbelleği için zaman sabiti 
const API_CACHE_DURATION = 60000; // 1 dakika (ms)
const API_CACHE = new Map<string, { data: any, timestamp: number }>();

// API Cache temizleme fonksiyonu
const clearExpiredCache = () => {
  const now = Date.now();
  for (const [key, value] of API_CACHE.entries()) {
    if (now - value.timestamp > API_CACHE_DURATION) {
      API_CACHE.delete(key);
    }
  }
};

// 5 dakikada bir önbelleği temizle
setInterval(clearExpiredCache, 300000); // 5 dakika (ms)

// Timeout değerleri (ms)
const SHORT_TIMEOUT = 30000;  // 30 saniye
const MEDIUM_TIMEOUT = 45000; // 45 saniye
const LONG_TIMEOUT = 60000;   // 60 saniye

// Yeniden deneme ayarları
const MAX_RETRY_COUNT = 2;
const RETRY_DELAY = 1000; // 1 saniye

export const useApi = () => {
  const { currentCity } = useCity();
  
  // API URL'yi alma fonksiyonu - loglama iyileştirildi
  const getBaseUrl = () => {
    if (!currentCity) {
      logApiError('getBaseUrl', 'Şehir seçilmedi! API URL alınamıyor.');
      return null;
    }
    
    // API URL'yi al
    const apiUrl = currentCity.apiEndpoint;
    
    // Sadece ilk kez veya özel durumlarda logla
    if (!apiUrlLogShown && apiUrl) {
      console.log(`API URL alınıyor - Şehir: ${currentCity.cityName}, URL: ${apiUrl}`);
      apiUrlLogShown = true;
    }
    
    // API URL yoksa veya boşsa hata logla
    if (!apiUrl || apiUrl === '') {
      logApiError('getBaseUrl', `${currentCity.cityName} şehri için API URL tanımlı değil!`);
      return null;
    }
    
    return apiUrl;
  };
  
  const baseUrl = getBaseUrl();
  
  const getServiceUrl = (endpoint: string) => {
    if (!currentCity) {
      throw new Error('Şehir seçilmedi! API çağrısı yapılamaz.');
    }
    
    if (!baseUrl) {
      logApiError('getServiceUrl', `${currentCity.cityName} şehri için API URL bulunamadı!`);
      throw new Error(`Şehir için API URL tanımlanmamış! (Şehir: ${currentCity.cityName})`);
    }
    
    // URL oluşturma - çift slash kontrolünü iyileştirelim
    // baseUrl'den son eğik çizgiyi temizle
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    // endpoint'ten başlangıç eğik çizgisini temizle veya ekle
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    
    // Temizlenmiş URL'leri birleştir
    const url = `${cleanBaseUrl}${cleanEndpoint}`;
    
    // Sadece belirli sayıda log göster ve sonra durdur
    if (serviceUrlCounter < MAX_URL_LOGS) {
      console.log(`API URL oluşturuldu: ${url}`);
      serviceUrlCounter++;
      
      // Son logdan sonra bir bilgi mesajı ekle
      if (serviceUrlCounter === MAX_URL_LOGS) {
        console.log("API URL logları kısıtlandı - performans için sonraki URL'ler gösterilmeyecek");
      }
    }
    
    return url;
  };

  // Önbellekteki veriyi kontrol et ve geçerliyse kullan
  const checkCache = (cacheKey: string) => {
    const cachedItem = API_CACHE.get(cacheKey);
    if (cachedItem) {
      const now = Date.now();
      if (now - cachedItem.timestamp < API_CACHE_DURATION) {
        logApiResponse(cacheKey, 'cache-hit', cachedItem.data);
        return cachedItem.data;
      }
    }
    return null;
  };

  // Veriyi önbelleğe ekle
  const addToCache = (cacheKey: string, data: any) => {
    API_CACHE.set(cacheKey, {
      data,
      timestamp: Date.now()
    });
  };
  
  // Tüm otobüs hatlarını alma
  const getBusLines = async (params?: { search?: string; mode?: string }) => {
    try {
      // CurrentCity yoksa hata fırlat
      if (!currentCity) {
        throw new Error('Şehir seçilmedi! API çağrısı yapılamaz.');
      }

      // API URL oluştur
      const url = new URL(getServiceUrl('/api/bus/lines'));
      
      // Query parametrelerini ekle
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined) {
            url.searchParams.append(key, value.toString());
          }
        });
      }
      
      // Önbellek anahtarı oluştur
      const cacheKey = `bus_lines_${url.toString()}`;
      
      // Önbellekte veri var mı kontrol et
      const cachedData = checkCache(cacheKey);
      if (cachedData) {
        return cachedData;
      }
      
      // API çağrısını logla
      logApiRequest('getBusLines', url.toString(), params);
      
      // Timeout süresini artıralım
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), MEDIUM_TIMEOUT); // Daha uzun süre
      
      try {
        // API isteği yap
        const response = await fetch(url.toString(), {
          signal: controller.signal,
          headers: {
            'Accept': 'application/json',
            'Cache-Control': 'no-cache'
          }
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          const errorText = await response.text();
          logApiError('getBusLines', `API yanıtı başarısız: ${response.status} ${errorText}`);
          throw new Error(`Hatlar yüklenirken hata oluştu: ${response.status} ${response.statusText}`);
        }
        
        const data = await response.json();
        
        // API yanıtını logla
        logApiResponse('getBusLines', 'success', { status: data.status, count: data.data?.length || 0 });
        
        // Başarılı veriyi önbelleğe al
        if (data && data.status === "success") {
          addToCache(cacheKey, data);
        }
        
        return data;
      } catch (fetchErr: any) {
        if (fetchErr.name === 'AbortError') {
          logApiError('getBusLines', 'API isteği zaman aşımına uğradı');
          throw new Error('İstek zaman aşımına uğradı, lütfen daha sonra tekrar deneyin');
        }
        throw fetchErr;
      }
    } catch (err: any) {
      logApiError('getBusLines', err);
      
      // URL hatalarını daha açıklayıcı hale getirelim
      if (err.message && (err.message.includes('API URL tanımlanmamış') || err.message.includes('API URL bilgisi bulunamadı'))) {
        err.message = `Şehir API adresi tanımlanmamış. Lütfen sistem yöneticisiyle iletişime geçin. (Şehir: ${currentCity?.cityName})`;
      }
      
      throw err;
    }
  };

  // Tek bir otobüs hattının detaylarını alma - yeniden deneme mantığıyla geliştirildi
  const getBusLine = async (lineId: string, retryCount = 0, forceRefresh = false) => {
    try {
      // CurrentCity yoksa hata fırlat
      if (!currentCity) {
        throw new Error('Şehir seçilmedi! API çağrısı yapılamaz.');
      }

      // Önbellek anahtarı oluştur
      const cacheKey = `bus_line_${lineId}`;
      
      // Önbellekte veri var mı kontrol et (forceRefresh true ise önbelleği atla)
      if (!forceRefresh) {
        const cachedData = checkCache(cacheKey);
        if (cachedData) {
          return cachedData;
        }
      }

      const url = new URL(getServiceUrl(`/api/bus/lines/${lineId}`));
      
      // API çağrısını logla
      logApiRequest('getBusLine', url.toString(), { lineId, retryAttempt: retryCount });
      
      // Timeout süresini artırdık
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), LONG_TIMEOUT); // 60 saniye
      
      try {
        const response = await fetch(url.toString(), {
          signal: controller.signal,
          headers: {
            'Accept': 'application/json',
            'Cache-Control': forceRefresh ? 'no-cache, no-store' : 'no-cache'
          }
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          const errorText = await response.text();
          logApiError('getBusLine', `API yanıtı başarısız: ${response.status} ${errorText}`);
          throw new Error(`Hat detayı yüklenirken hata oluştu: ${response.status} ${response.statusText}`);
        }
        
        const data = await response.json();
        
        // API yanıtını logla
        logApiResponse('getBusLine', 'success', { status: data.status, lineId });
        
        // Veriyi önbelleğe ekle
        if (data && data.status === "success") {
          addToCache(cacheKey, data);
        }
        
        return data;
      } catch (fetchErr: any) {
        if (fetchErr.name === 'AbortError') {
          logApiError('getBusLine', 'API isteği zaman aşımına uğradı');
          
          // Yeniden deneme mantığı
          if (retryCount < MAX_RETRY_COUNT) {
            logApiRequest('getBusLine', 'Yeniden deneniyor', { lineId, retryAttempt: retryCount + 1 });
            
            // Belirli bir süre bekle ve yeniden dene
            await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
            return getBusLine(lineId, retryCount + 1, forceRefresh);
          }
          
          throw new Error('İstek zaman aşımına uğradı, sunucu yanıt vermiyor. Lütfen daha sonra tekrar deneyin');
        }
        throw fetchErr;
      }
    } catch (err: any) {
      logApiError('getBusLine', err);
      throw err;
    }
  };
  
  // Durakları alma
  const getBusStops = async (params?: { limit?: number; offset?: number; search?: string }) => {
    try {
      // CurrentCity yoksa hata fırlat
      if (!currentCity) {
        throw new Error('Şehir seçilmedi! API çağrısı yapılamaz.');
      }

      const url = new URL(getServiceUrl('/api/bus/stops'));
      
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined) {
            url.searchParams.append(key, value.toString());
          }
        });
      }
      
      console.log('Duraklar API isteği yapılıyor:', url.toString());
      
      const response = await fetch(url.toString());
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API yanıtı başarısız:', response.status, errorText);
        throw new Error(`Duraklar yüklenirken hata oluştu: ${response.status} ${response.statusText}`);
      }
      
      return response.json();
    } catch (err: any) {
      console.error('Duraklar API çağrısında hata:', err);
      
      // URL hatalarını daha açıklayıcı hale getirelim
      if (err.message && (err.message.includes('API URL tanımlanmamış') || err.message.includes('API URL bilgisi bulunamadı'))) {
        err.message = `Şehir API adresi tanımlanmamış. Lütfen sistem yöneticisiyle iletişime geçin. (Şehir: ${currentCity?.cityName})`;
      }
      
      throw err;
    }
  };

  // Durak detaylarını alma - timeout artırıldı
  const getBusStopDetail = async (stopId: string) => {
    try {
      // CurrentCity yoksa hata fırlat
      if (!currentCity) {
        throw new Error('Şehir seçilmedi! API çağrısı yapılamaz.');
      }

      const url = new URL(getServiceUrl(`/api/bus/stops/${stopId}`));
      
      console.log('Durak detayı API isteği yapılıyor:', url.toString());
      
      // Talep için timeout süresini artırdık
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), MEDIUM_TIMEOUT); // 45 saniye
      
      try {
        const response = await fetch(url.toString(), {
          signal: controller.signal,
          headers: {
            'Accept': 'application/json',
            'Cache-Control': 'no-cache'
          }
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('API yanıtı başarısız:', response.status, errorText);
          throw new Error(`Durak detayı yüklenirken hata oluştu: ${response.status} ${response.statusText}`);
        }
        
        const data = await response.json();
        
        // Başarılı veriyi önbelleğe almıyoruz (önbellek sorununu çözmek için)
        console.log('Durak detayı API yanıtı alındı');
        
        return data;
      } catch (fetchErr: any) {
        if (fetchErr.name === 'AbortError') {
          console.error('API isteği zaman aşımına uğradı');
          throw new Error('İstek zaman aşımına uğradı, lütfen daha sonra tekrar deneyin');
        }
        throw fetchErr;
      }
    } catch (err: any) {
      console.error('Durak detayı API çağrısında hata:', err);
      throw err;
    }
  };
  
  // Hat güzergah bilgilerini alma
  const getBusLinePath = async (lineId: string) => {
    try {
      // CurrentCity yoksa hata fırlat
      if (!currentCity) {
        throw new Error('Şehir seçilmedi! API çağrısı yapılamaz.');
      }

      const url = new URL(getServiceUrl(`/api/bus/lines/${lineId}/path`));
      
      console.log('Hat güzergahı API isteği yapılıyor:', url.toString());
      
      const response = await fetch(url.toString());
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API yanıtı başarısız:', response.status, errorText);
        throw new Error(`Hat güzergahı yüklenirken hata oluştu: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Hat güzergahı API yanıtı alındı:', data);
      return data;
    } catch (err: any) {
      console.error('Hat güzergahı API çağrısında hata:', err);
      throw err;
    }
  };

  // Otobüs hattı durakları alma
  const getBusLineStops = async (lineId: string) => {
    try {
      // CurrentCity yoksa hata fırlat
      if (!currentCity) {
        throw new Error('Şehir seçilmedi! API çağrısı yapılamaz.');
      }

      const url = new URL(getServiceUrl(`/api/bus/lines/${lineId}/stops`));
      
      console.log('Hat durakları API isteği yapılıyor:', url.toString());
      
      const response = await fetch(url.toString());
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API yanıtı başarısız:', response.status, errorText);
        throw new Error(`Hat durakları yüklenirken hata oluştu: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Hat durakları API yanıtı alındı:', data);
      return data;
    } catch (err: any) {
      console.error('Hat durakları API çağrısında hata:', err);
      throw err;
    }
  };

  // Ulaşım moduna göre hat detaylarını al
  const getTransportLine = async (transportMode: string, lineId: string) => {
    try {
      console.log(`getTransportLine çağrıldı: mod=${transportMode}, hat=${lineId}`);
      
      const baseUrl = getBaseUrl();
      if (!baseUrl) {
        console.error('API adresi tanımlanmamış');
        throw new Error('API adresi tanımlanmamış');
      }

      // URL oluşturma - düzeltilmiş yöntem
      const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
      const url = `${cleanBaseUrl}/api/${transportMode}/lines/${lineId}`;
      console.log('Hat detayı istek URL:', url);
      
      // Timeout kontrolü için
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), SHORT_TIMEOUT);
      
      try {
        const response = await fetch(url, {
          signal: controller.signal,
          headers: {
            'Accept': 'application/json',
            'Cache-Control': 'no-cache'
          }
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error(`Hat detayı API hatası: ${response.status}`, errorText);
          throw new Error(`Hata: ${response.status} ${response.statusText}`);
        }
        
        const data = await response.json();
        console.log('Hat detayı API yanıtı alındı:', data);
        return data;
      } catch (fetchErr: any) {
        if (fetchErr.name === 'AbortError') {
          console.error('API isteği zaman aşımına uğradı');
          throw new Error('İstek zaman aşımına uğradı, lütfen daha sonra tekrar deneyin');
        }
        throw fetchErr;
      }
    } catch (err: any) {
      console.error('getTransportLine hatası:', err);
      throw err;
    }
  };

  // Ulaşım moduna göre hat güzergahını al
  const getTransportLinePath = async (transportMode: string, lineId: string) => {
    try {
      console.log(`getTransportLinePath çağrıldı: mod=${transportMode}, hat=${lineId}`);
      
      const baseUrl = getBaseUrl();
      if (!baseUrl) {
        console.error('API adresi tanımlanmamış');
        throw new Error('API adresi tanımlanmamış');
      }

      // URL oluşturma - düzeltilmiş yöntem
      const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
      const url = `${cleanBaseUrl}/api/${transportMode}/lines/${lineId}/path`;
      console.log('Hat güzergahı istek URL:', url);
      
      // Timeout kontrolü için
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), SHORT_TIMEOUT);
      
      try {
        const response = await fetch(url, {
          signal: controller.signal,
          headers: {
            'Accept': 'application/json',
            'Cache-Control': 'no-cache'
          }
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error(`Hat güzergahı API hatası: ${response.status}`, errorText);
          throw new Error(`Hata: ${response.status} ${response.statusText}`);
        }
        
        const data = await response.json();
        console.log('Hat güzergahı API yanıtı alındı:', data);
        return data;
      } catch (fetchErr: any) {
        if (fetchErr.name === 'AbortError') {
          console.error('API isteği zaman aşımına uğradı');
          throw new Error('İstek zaman aşımına uğradı, lütfen daha sonra tekrar deneyin');
        }
        throw fetchErr;
      }
    } catch (err: any) {
      console.error('getTransportLinePath hatası:', err);
      throw err;
    }
  };
  
  return {
    getBusLines,
    getBusLine,
    getBusStops,
    getBusStopDetail,
    getBusLinePath,
    getTransportLine,
    getTransportLinePath,
    getBusLineStops,
    clearCache: () => API_CACHE.clear(), // Önbelleği temizleme fonksiyonu ekledik
  };
};
